/**
 * Account API Key Service
 *
 * Manages the N api-keys an account can own (table `account_api_keys`, admin
 * API, SuperUser only). Each key is optionally bound to ONE application:
 *   - applicationId null  → "account" key, valid for every app of the account.
 *   - applicationId set   → the key determines the app of the send.
 *
 * Endpoints (base URL already `/api` in httpClient, envelope `{ Response }`):
 *   GET    /account/{accountId}/api-keys
 *   POST   /account/{accountId}/api-keys        → also returns `apiKey` (once)
 *   PUT    /account/api-keys/{keyId}
 *   POST   /account/api-keys/{keyId}/rotate     → returns new `apiKey` (once)
 *   PUT    /account/api-keys/{keyId}/active
 *   DELETE /account/api-keys/{keyId}
 *
 * The plaintext `apiKey` is only ever returned by create/rotate and must be
 * revealed to the operator once — it is never persisted client-side.
 */

import { httpClient } from '@/utils/httpClient';
import type { NotifyEnvelope } from '@/types/api';

/**
 * Lo que una key alcanza. Es un CONJUNTO, no un valor: el backend lo guarda separado
 * por comas ('send,automations') porque un PMS necesita las automatizaciones y a veces
 * tambien enviar, y darle 'manage' para eso le abriria de paso los registros de
 * entrega, los creditos, los datos de la cuenta y la centralita.
 *
 * 'manage' va siempre solo: ya lo abre todo.
 */
export type ApiKeyScope = 'manage' | 'send' | 'automations';

/** Los ambitos limitados, en el orden en que se pintan y se guardan. */
export const LIMITED_SCOPES: ApiKeyScope[] = ['send', 'automations'];

/**
 * El texto del backend a lista. Lo que no reconocemos se conserva tal cual: una key
 * antigua con un valor raro tiene que poder verse y editarse, no desaparecer.
 */
export const parseScopes = (scope: string | null | undefined): string[] =>
  (scope ?? '')
    .split(',')
    .map((s) => s.trim().toLowerCase())
    .filter((s) => s.length > 0);

/** De vuelta a lo que espera el backend, en el mismo orden fijo que el normaliza. */
export const joinScopes = (scopes: string[]): string =>
  scopes.includes('manage')
    ? 'manage'
    : LIMITED_SCOPES.filter((s) => scopes.includes(s)).join(',');

export interface AccountApiKey {
  id: string;
  label: string;
  applicationId: string | null;
  applicationName: string | null;
  /** Uno o varios ambitos separados por comas. Usa parseScopes para leerlo. */
  scope: string;
  isTest: boolean;
  isActive: boolean;
  createdAt: string;
}

/** Create/rotate responses carry the plaintext secret exactly once. */
export interface AccountApiKeyWithSecret extends AccountApiKey {
  apiKey: string;
}

export interface AccountApiKeyPayload {
  label: string;
  applicationId: string | null;
  /** Uno o varios ambitos separados por comas; se compone con joinScopes. */
  scope: string;
  isTest: boolean;
}

export const listAccountApiKeys = async (
  accountId: string,
): Promise<AccountApiKey[]> => {
  const { data } = await httpClient.get<NotifyEnvelope<AccountApiKey[]>>(
    `/account/${encodeURIComponent(accountId)}/api-keys`,
    { skipGlobalLoader: true },
  );
  return data.Response ?? [];
};

export const createAccountApiKey = async (
  accountId: string,
  body: AccountApiKeyPayload,
): Promise<AccountApiKeyWithSecret> => {
  const { data } = await httpClient.post<NotifyEnvelope<AccountApiKeyWithSecret>>(
    `/account/${encodeURIComponent(accountId)}/api-keys`,
    body,
  );
  return data.Response;
};

export const updateAccountApiKey = async (
  keyId: string,
  body: AccountApiKeyPayload,
): Promise<AccountApiKey> => {
  const { data } = await httpClient.put<NotifyEnvelope<AccountApiKey>>(
    `/account/api-keys/${encodeURIComponent(keyId)}`,
    body,
  );
  return data.Response;
};

export const rotateAccountApiKey = async (
  keyId: string,
): Promise<AccountApiKeyWithSecret> => {
  const { data } = await httpClient.post<NotifyEnvelope<AccountApiKeyWithSecret>>(
    `/account/api-keys/${encodeURIComponent(keyId)}/rotate`,
  );
  return data.Response;
};

export const setAccountApiKeyActive = async (
  keyId: string,
  active: boolean,
): Promise<void> => {
  await httpClient.put<NotifyEnvelope<unknown> | undefined>(
    `/account/api-keys/${encodeURIComponent(keyId)}/active`,
    { active },
  );
};

export const deleteAccountApiKey = async (keyId: string): Promise<void> => {
  await httpClient.delete<NotifyEnvelope<unknown> | undefined>(
    `/account/api-keys/${encodeURIComponent(keyId)}`,
  );
};
